import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Wrench, Plus, Trash2, Calendar, AlertTriangle, Truck as TruckIcon } from "lucide-react";

const TYPES = ["service", "tyres", "inspection", "registration", "insurance", "other"];

export default function Maintenance() {
  const [events, setEvents] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ truck_id: "", type: "service", date: "", next_due_date: "", hours: "", km: "", cost: "", supplier: "", notes: "" });
  const [filterTruck, setFilterTruck] = useState("all");

  const load = async () => {
    const [eRes, uRes, tRes] = await Promise.all([
      api.get("/maintenance/events"),
      api.get("/maintenance/upcoming"),
      api.get("/trucks"),
    ]);
    setEvents(eRes.data || []);
    setUpcoming(uRes.data || []);
    setTrucks(tRes.data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (filterTruck === "all" ? events : events.filter((e) => e.truck_id === filterTruck)), [events, filterTruck]);

  const openAdd = (truck_id = "") => {
    setForm({ truck_id, type: "service", date: new Date().toISOString().slice(0, 10), next_due_date: "", hours: "", km: "", cost: "", supplier: "", notes: "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      const body = { ...form };
      ["hours", "km", "cost"].forEach((k) => { body[k] = body[k] === "" ? null : Number(body[k]); });
      if (body.next_due_date === "") body.next_due_date = null;
      if (body.supplier === "") body.supplier = null;
      if (body.notes === "") body.notes = null;
      await api.post("/maintenance/events", body);
      toast.success("Maintenance logged");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this maintenance record?")) return;
    await api.delete(`/maintenance/events/${id}`);
    toast.success("Deleted");
    load();
  };

  const tone = (status) =>
    status === "overdue" ? "text-red-500 bg-red-500/10 border-red-500/30"
    : status === "due_soon" ? "text-amber-500 bg-amber-500/10 border-amber-500/30"
    : "text-sky-500 bg-sky-500/10 border-sky-500/30";

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="maintenance-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag">FLEET · MAINTENANCE</div>
          <h1 className="heading text-3xl sm:text-4xl font-black uppercase tracking-tight">
            Service <span className="text-primary-orange">Log</span>
          </h1>
          <p className="text-muted-industrial text-sm mt-1">Track every service, inspection &amp; registration renewal.</p>
        </div>
        <Button data-testid="add-maintenance-btn" onClick={() => openAdd()} className="btn-cta inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Log Event
        </Button>
      </div>

      {/* Upcoming */}
      <Card className="card-v2 p-5" data-testid="upcoming-maintenance">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-primary-orange" />
          <div className="label-tag">Upcoming · Next 30 days</div>
          <span className="mono text-[10px] uppercase text-muted-industrial">{upcoming.length} items</span>
        </div>
        {upcoming.length === 0 ? (
          <div className="text-sm text-muted-industrial">Nothing due in the next 30 days. 🎉</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcoming.map((u, i) => (
              <div key={i} className={`border rounded-lg p-3 ${tone(u.status)}`} data-testid={`upcoming-item-${i}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest opacity-80">{u.type.replace("_", " ")}</div>
                    <div className="heading font-black text-lg mt-1">Truck {u.truck_number}</div>
                    <div className="text-xs opacity-80 mt-1 mono">
                      {u.next_due_date} · {u.days_until < 0 ? `${Math.abs(u.days_until)}d overdue` : `${u.days_until}d away`}
                    </div>
                  </div>
                  <button
                    onClick={() => openAdd(u.truck_id)}
                    className="text-[10px] uppercase mono border border-current px-2 py-1 rounded hover:bg-current hover:bg-opacity-10"
                    data-testid={`log-service-${u.truck_id}`}
                  >
                    Log
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Filter + list */}
      <div className="flex items-center gap-3">
        <Label className="label-tag">Filter by truck</Label>
        <Select value={filterTruck} onValueChange={setFilterTruck}>
          <SelectTrigger data-testid="filter-truck" className="w-56 rounded-lg bg-transparent border-industrial mono h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-lg bg-surface border-industrial">
            <SelectItem value="all">All trucks</SelectItem>
            {trucks.map((t) => <SelectItem key={t.id} value={t.id}>{t.number} · {t.rego}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="card-v2 p-5" data-testid="maintenance-list">
        {filtered.length === 0 ? (
          <div className="text-sm text-muted-industrial text-center py-6">No maintenance records yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-industrial">
                <th className="label-tag pb-2">Date</th>
                <th className="label-tag pb-2">Truck</th>
                <th className="label-tag pb-2">Type</th>
                <th className="label-tag pb-2">Next Due</th>
                <th className="label-tag pb-2 text-right">Hours</th>
                <th className="label-tag pb-2 text-right">km</th>
                <th className="label-tag pb-2 text-right">Cost</th>
                <th className="label-tag pb-2">Supplier / Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-t border-industrial hover:bg-surface-2" data-testid={`m-row-${e.id}`}>
                  <td className="py-2 mono">{e.date}</td>
                  <td className="py-2 heading font-bold">{e.truck_number || "—"}</td>
                  <td className="py-2 mono text-xs uppercase text-primary-orange">{e.type}</td>
                  <td className="py-2 mono">{e.next_due_date || "—"}</td>
                  <td className="py-2 text-right mono">{e.hours ?? "—"}</td>
                  <td className="py-2 text-right mono">{e.km ?? "—"}</td>
                  <td className="py-2 text-right mono">{e.cost != null ? `$${Number(e.cost).toFixed(0)}` : "—"}</td>
                  <td className="py-2 text-xs text-muted-industrial truncate max-w-[220px]">{[e.supplier, e.notes].filter(Boolean).join(" · ")}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => del(e.id)} className="text-muted-industrial hover:text-red-500" data-testid={`m-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-industrial max-w-lg" data-testid="maintenance-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase flex items-center gap-2">
              <Wrench className="w-4 h-4 text-primary-orange" /> Log Maintenance
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="label-tag">Truck</Label>
              <Select value={form.truck_id} onValueChange={(v) => setForm({ ...form, truck_id: v })}>
                <SelectTrigger data-testid="form-truck" className="rounded-lg bg-transparent border-industrial mono h-10">
                  <SelectValue placeholder="Pick a truck" />
                </SelectTrigger>
                <SelectContent className="rounded-lg bg-surface border-industrial">
                  {trucks.map((t) => <SelectItem key={t.id} value={t.id}>{t.number} · {t.rego}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="form-type" className="rounded-lg bg-transparent border-industrial mono h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg bg-surface border-industrial">
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="label-tag">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-lg bg-transparent border-industrial mono h-10" /></div>
            <div><Label className="label-tag">Next Due</Label><Input type="date" value={form.next_due_date} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} className="rounded-lg bg-transparent border-industrial mono h-10" /></div>
            <div><Label className="label-tag">Hours</Label><Input type="number" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="rounded-lg bg-transparent border-industrial mono h-10" /></div>
            <div><Label className="label-tag">km</Label><Input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} className="rounded-lg bg-transparent border-industrial mono h-10" /></div>
            <div><Label className="label-tag">Cost $</Label><Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="rounded-lg bg-transparent border-industrial mono h-10" /></div>
            <div><Label className="label-tag">Supplier</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="rounded-lg bg-transparent border-industrial mono h-10" /></div>
            <div className="col-span-2"><Label className="label-tag">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-lg bg-transparent border-industrial mono min-h-[70px]" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-lg">Cancel</Button>
            <Button data-testid="save-maintenance" onClick={save} disabled={!form.truck_id || !form.date} className="btn-cta">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
